import { Injectable } from '@angular/core';
import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { CompanyService } from './company.service';
import { UserService } from './user.service';
import { combineLatest, filter, Observable } from 'rxjs';
import { CompanyData, EmployeeData } from '../interfaces';
import { ACTION, BILLING_STATUS, EMPLOYEE_ROLES, SUBJECT } from '../enums';

export type RoleAbility = MongoAbility<[ACTION, SUBJECT]>;
export type SubscriptionAbility = MongoAbility<[ACTION, SUBJECT]>;


@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  readonly roleAbility: RoleAbility = createMongoAbility([], { anyAction: ACTION.MANAGE, anySubjectType: SUBJECT.ALL });
  readonly subscriptionAbility: SubscriptionAbility = createMongoAbility([], {
    anyAction: ACTION.MANAGE,
    anySubjectType: SUBJECT.ALL
  });

  readonly roleAbility$: Observable<RoleAbility> = new Observable((observer) => {
    observer.next(this.roleAbility);
    return this.roleAbility.on('updated', () => observer.next(this.roleAbility));
  });
  readonly subscriptionAbility$: Observable<SubscriptionAbility> = new Observable((observer) => {
    observer.next(this.subscriptionAbility);
    return this.subscriptionAbility.on('updated', () => observer.next(this.subscriptionAbility));
  });
  readonly abilities$ = combineLatest({
    role: this.roleAbility$,
    subscription: this.subscriptionAbility$
  });

  constructor(
    private companyService: CompanyService,
    private userService: UserService
  ) {
    this.init();
  }

  private init() {
    combineLatest({
      company: this.companyService.companyInfo$.pipe(filter((company) => company !== null)),
      profile: this.userService.profileInfo$.pipe(filter((profile) => !!profile.roles?.length))
    })
      .subscribe(({ company, profile }) => {
        this.updateRolePermissions(company!, profile);
        this.updateSubscriptionPermissions(company!);
      });
  }

  private updateSubscriptionPermissions(company: CompanyData) {
    const { can, cannot, rules } = new AbilityBuilder<SubscriptionAbility>(createMongoAbility);

    can(ACTION.MANAGE, SUBJECT.ALL);

    if (!company.billing?.status || [BILLING_STATUS.SUSPENDED, BILLING_STATUS.UNSUBSCRIBED].includes(company.billing.status)) {
      cannot([ACTION.EDIT, ACTION.CREATE, ACTION.DELETE], SUBJECT.ALL);
    }

    if (this.companyService.isEmployeesLimitExceed()) {
      cannot([ACTION.EDIT, ACTION.CREATE, ACTION.DELETE], SUBJECT.ALL);
    }

    this.subscriptionAbility.update(rules);
  }

  private updateRolePermissions(company: CompanyData, profile: EmployeeData) {
    const { can, rules } = new AbilityBuilder<RoleAbility>(createMongoAbility);
    if (profile.roles?.includes(EMPLOYEE_ROLES.EMPLOYEE)) {
      can([ACTION.VIEW, ACTION.CREATE], SUBJECT.CALENDAR_DATE_LEAVE);
      // Conditional permission is only available when using data models as subjects. Consider using it in the future.
      // can(ACTION.DELETE, SUBJECT.CALENDAR_DATE_LEAVE, { id: profile.id })
      can([ACTION.VIEW], SUBJECT.CALENDAR_DATE_HOLIDAY);
    }

    if (profile.roles?.includes(EMPLOYEE_ROLES.HR_MANAGER)) {
      can(ACTION.MANAGE, SUBJECT.CALENDAR_DATE_LEAVE);
      can(ACTION.MANAGE, SUBJECT.CALENDAR_DATE_HOLIDAY);
      can(ACTION.MANAGE, SUBJECT.EMPLOYEE);
    }

    if (profile.roles?.includes(EMPLOYEE_ROLES.MANAGER)) {
      can(ACTION.EDIT, SUBJECT.CALENDAR_DATE_LEAVE);
      can(ACTION.EDIT, SUBJECT.CALENDAR_DATE_HOLIDAY);
    }

    if (profile.roles?.includes(EMPLOYEE_ROLES.INFO_MANAGER)) {
      can(ACTION.MANAGE, SUBJECT.CALENDAR_DATE_HOLIDAY);

      can(ACTION.MANAGE, SUBJECT.NEWS_POST);
    }

    if (profile.roles?.includes(EMPLOYEE_ROLES.DEVICE_MANAGER)) {
      can(ACTION.MANAGE, SUBJECT.DEVICE);
    }

    if (profile.roles?.includes(EMPLOYEE_ROLES.COMPANY_MANAGER)) {
      can(ACTION.MANAGE, SUBJECT.COMPANY);
    }

    if (profile.userAccountId === company.ownerUserAccountId) {
      // Allow only the company owner to manage the subscription
      can(ACTION.MANAGE, SUBJECT.SUBSCRIPTION);
    }

    this.roleAbility.update(rules);
  }
}
